import { FC, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { filter, isEmpty, keyBy } from 'lodash';

import {
  ConfigurationsValues,
  MappingsContext,
  NoteTypesDto,
  NoteTypesTransformedDto,
} from '@app/contexts';
import {
  QUERY_KEYS,
  useFeatureFlag,
  usePatronSession,
  useSavedInstancesCompact,
} from '@app/hooks';
import {
  getOpacConfigurationsQuery,
  makeRequest,
  OpacConfig,
  parseJSON,
} from '@app/utils';
import { FEATURE, ItemStatusDto, LOGIN_METHODS } from '@app/constants';

const REQUIRED_CONFIGS: OpacConfig[] = [
  {
    module: 'OPAC_TOOLS',
    configName: 'ENABLE_STACKMAP',
    code: 'ENABLE_STACKMAP',
    getConfigValue: config => ({
      isStackMapEnabled: config?.value === 'true',
    }),
  },
  {
    module: 'OPAC_CONFIGURATION',
    configName: 'opac_auth_option',
    code: 'OPAC_AUTH_OPTION',
    getConfigValue: config => {
      const loginMethod = config?.value;

      return {
        isSSOLogin: loginMethod === LOGIN_METHODS.SSO,
        isNativeLogin: loginMethod === LOGIN_METHODS.NATIVE,
      };
    },
  },
  {
    module: 'OPAC_TOOLS',
    configName: 'library_location_map',
    code: 'LIBRARY_LOCATION_MAP',
    getConfigValue: config => ({
      libraryLocationMap: parseJSON(config?.value, {}),
    }),
  },
];

export const MappingsProvider: FC = ({ children }) => {
  const { externalSystemId } = usePatronSession();
  const { isActive: isHoldsAndCheckoutsFlagActive } = useFeatureFlag({
    name: FEATURE.holdsAndCheckouts,
    isActiveIfNotFound: true,
  });

  const createQueryKey = useCallback(
    key => [key, externalSystemId],
    [externalSystemId]
  );

  const notesTypesQuery = useQuery<NoteTypesDto, null, NoteTypesTransformedDto>(
    createQueryKey('noteTypes'),
    () => makeRequest({ url: '/opac-inventory/note-types-mappings' }),
    {
      select: data =>
        keyBy(filter(data.noteTypesForDisplay, { active: true }), 'labelKey'),
    }
  );

  const userItemStatusesQuery = useQuery(
    createQueryKey(QUERY_KEYS.userItemStatuses),
    () =>
      makeRequest<ItemStatusDto>({
        url: `/opac-patron/account/${externalSystemId}`,
        params: {
          includeHolds: true,
          includeCharges: true,
          includeLoans: true,
        },
      }),
    { enabled: !isEmpty(externalSystemId) && isHoldsAndCheckoutsFlagActive }
  );

  const configurationsQuery = useQuery(createQueryKey('configurations'), () =>
    makeRequest({
      url: '/opac-configurations/entries',
      params: {
        query: getOpacConfigurationsQuery(REQUIRED_CONFIGS),
      },
    }).then(({ configs }) =>
      REQUIRED_CONFIGS.reduce((configsValues, { code, getConfigValue }) => {
        const currentConfig = configs?.find(config => config.code === code);

        return Object.assign(configsValues, getConfigValue(currentConfig));
      }, {})
    )
  );

  const value = useMemo(
    () => ({
      userItemStatuses: userItemStatusesQuery.data,
      noteTypes: notesTypesQuery.data,
      areUserItemStatusesLoading: userItemStatusesQuery.isFetching,
      configurations: {
        ...(configurationsQuery.data as ConfigurationsValues),
        configurationsQuery,
      },
    }),
    [
      notesTypesQuery.data,
      userItemStatusesQuery.data,
      userItemStatusesQuery.isFetching,
      configurationsQuery,
    ]
  );

  // TODO: Fetch compact saved instances list only once on page load
  useSavedInstancesCompact();

  return (
    <MappingsContext.Provider value={value}>
      {children}
    </MappingsContext.Provider>
  );
};
